/**
 * Ludo Dice Component
 *
 * Renders a die with rolling animation and current value (1–6 dots).
 * Click/touch to trigger roll; disabled when not player's turn.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { PlayerColor } from '../types';
import './LudoDice.css';

interface LudoDiceProps {
  value: number | null;
  rolling: boolean;
  onRoll: () => void;
  disabled: boolean;
  playerColor: PlayerColor;
}

const DOT_POSITIONS: Record<number, number[][]> = {
  1: [[50, 50]],
  2: [[25, 25], [75, 75]],
  3: [[25, 25], [50, 50], [75, 75]],
  4: [[25, 25], [75, 25], [25, 75], [75, 75]],
  5: [[25, 25], [75, 25], [50, 50], [25, 75], [75, 75]],
  6: [[25, 25], [75, 25], [25, 50], [75, 50], [25, 75], [75, 75]],
};

const COLOR_BORDERS: Record<PlayerColor, string> = {
  red: 'var(--ludo-red, #e53935)',
  blue: 'var(--ludo-blue, #1e88e5)',
  green: 'var(--ludo-green, #43a047)',
  yellow: 'var(--ludo-yellow, #fdd835)',
};

const LudoDice: React.FC<LudoDiceProps> = ({
  value,
  rolling,
  onRoll,
  disabled,
  playerColor,
}) => {
  const [displayValue, setDisplayValue] = useState<number>(value || 1);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (!rolling) {
      if (value) setDisplayValue(value);
      setIsAnimating(false);
      return;
    }

    setIsAnimating(true);
    let frame = 0;
    const totalFrames = 10;
    const interval = setInterval(() => {
      setDisplayValue(Math.floor(Math.random() * 6) + 1);
      frame++;
      if (frame >= totalFrames) {
        clearInterval(interval);
        if (value) setDisplayValue(value);
        setIsAnimating(false);
      }
    }, 80);

    return () => clearInterval(interval);
  }, [rolling, value]);

  const handleClick = useCallback(() => {
    if (!disabled && !rolling) {
      onRoll();
    }
  }, [disabled, rolling, onRoll]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && !disabled && !rolling) {
        e.preventDefault();
        onRoll();
      }
    },
    [disabled, rolling, onRoll]
  );

  const dots = DOT_POSITIONS[displayValue] || DOT_POSITIONS[1];

  return (
    <div className="ludo-dice-container">
      <button
        className={`ludo-dice ${isAnimating ? 'ludo-dice--rolling' : ''} ${disabled ? 'ludo-dice--disabled' : ''}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label={`Dice showing ${displayValue}. ${disabled ? 'Not your turn.' : 'Click to roll.'}`}
        aria-live="polite"
        role="button"
        tabIndex={0}
        style={{ borderColor: COLOR_BORDERS[playerColor] }}
      >
        <svg viewBox="0 0 100 100" className="ludo-dice__face" aria-hidden="true">
          {dots.map(([cx, cy], i) => (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r="10"
              className="ludo-dice__dot"
            />
          ))}
        </svg>
      </button>
      {!disabled && !rolling && (
        <p className="ludo-dice__hint" aria-hidden="true">
          Tap to roll
        </p>
      )}
      {rolling && (
        <p className="ludo-dice__hint ludo-dice__hint--rolling" aria-live="assertive">
          Rolling...
        </p>
      )}
    </div>
  );
};

export { LudoDice };
export default LudoDice;
